import { useState, useEffect, useRef } from "react";
import { CheckCircle } from "lucide-react";
import { Dialog } from "../Dialog";
import TextArea from "../Textarea";
import { StarRating } from "./StarRating";
import { useFeedback } from "../../helpers/hooks/use-feedback";
import { useAnalytics } from "../../helpers/hooks/use-analytics";
import type { GeneralFeedbackDialogProps } from "./feedback.types";

export function GeneralFeedbackDialog({ open, onClose }: GeneralFeedbackDialogProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { mutateAsync, isLoading } = useFeedback();
  const { trackEvent } = useAnalytics();
  const hasTrackedOpen = useRef(false);
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout>>();

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setRating(0);
      setComment("");
      setError("");
      setSubmitted(false);
      hasTrackedOpen.current = false;
    }
    return () => clearTimeout(autoDismissTimer.current);
  }, [open]);

  // Track dialog shown
  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackEvent("feedback_dialog_shown", {
        type: "general",
        page: window.location.pathname,
      });
      hasTrackedOpen.current = true;
    }
  }, [open, trackEvent]);

  const handleSubmit = async () => {
    setError("");
    try {
      await mutateAsync({
        type: "general",
        rating,
        comment: comment.trim() || undefined,
        page: window.location.pathname,
      });
      trackEvent("feedback_submitted", {
        type: "general",
        rating,
        has_comment: !!comment.trim(),
        page: window.location.pathname,
      });
      setSubmitted(true);
      autoDismissTimer.current = setTimeout(() => onClose(), 2000);
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  const handleDismiss = () => {
    trackEvent("feedback_dismissed", {
      type: "general",
      page: window.location.pathname,
    });
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      variant="center"
      size="sm"
      onClose={handleDismiss}
      title={submitted ? undefined : "How are you enjoying Autumn?"}
      subtitle={submitted ? undefined : "Your feedback helps us improve"}
      secondaryAction={
        submitted
          ? undefined
          : { label: "Cancel", onClick: handleDismiss, variant: "ghost" }
      }
      primaryAction={
        submitted
          ? undefined
          : {
              label: "Submit",
              onClick: handleSubmit,
              disabled: rating === 0 || isLoading,
              loading: isLoading,
            }
      }
    >
      {submitted ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <CheckCircle className="text-brand-primary" size={40} />
          <p className="text-headline font-semibold text-content-primary">
            Thanks for your feedback!
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <StarRating value={rating} onChange={setRating} />
          </div>

          <TextArea
            label=""
            placeholder="Tell us more... (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />

          {error && <p className="text-danger text-xs">{error}</p>}
        </div>
      )}
    </Dialog>
  );
}
